import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Validation function (using plain JavaScript)
const validateForm = (formData) => {
  const errors = {};

  // Basic validation checks
  if (!formData.name || formData.name.length < 2) {
    errors.name = "Company name is required";
  }
  if (!formData.registrationNumber || formData.registrationNumber.length < 3) {
    errors.registrationNumber = "Registration number is required";
  }
  if (!formData.industryType || formData.industryType.length < 2) {
    errors.industryType = "Industry type is required";
  }

  // Contact info validation
  if (!formData.contactInfo.email || !/\S+@\S+\.\S+/.test(formData.contactInfo.email)) {
    errors['contactInfo.email'] = "Invalid email address";
  }
  if (!formData.contactInfo.phone || formData.contactInfo.phone.length < 8) {
    errors['contactInfo.phone'] = "Phone number is required";
  }

  // Address validation
  const addressFields = ['street', 'city', 'state', 'zipCode', 'country'];
  addressFields.forEach(field => {
    const path = `contactInfo.address.${field}`;
    if (!formData.contactInfo.address[field] || formData.contactInfo.address[field].length < 2) {
      errors[path] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
    }
  });

  // Employees validation
  if (!formData.employees[0].number || formData.employees[0].number.length === 0) {
    errors['employees.0.number'] = "Number of employees is required";
  }

  return errors;
};

const CompanyProfileModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    registrationNumber: '',
    industryType: '',
    contactInfo: {
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      }
    },
    employees: [{ number: '' }]
  });
  const [errors, setErrors] = useState({});
  const { state } = useAuth(); 
  const token = state.token;
  const userId = state.user?.id;

  useEffect(() => {
    if (!token || !userId) return;

    const checkCompanyProfile = async () => {
      try {
        const response = await apiService.get(`/api/profile/company/${userId}`, token);
        console.log('Existing profile:', response.data);
        // If profile exists, do not open modal
      } catch (error) {
        // If no profile exists, open modal
        setIsOpen(true);
      }
    };

    checkCompanyProfile();
  }, [token, userId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const keys = name.split('.');
    
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form data
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    try {
      setIsSubmitting(true);
      // Use the apiService to submit the profile data.
      const response = await apiService.post("/api/profile/company", token, formData);
      console.log('Profile created:', response.data);
      setIsOpen(false);
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          <div className="p-8">
            <h2 className="text-3xl font-bold text-[#732570] mb-4">
              Complete Your Company Profile
            </h2>
            <p className="text-gray-600 mb-6">
              Help us understand your business better by providing some essential details.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your Company Name"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#732570] ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Registration Number
                  </label>
                  <input
                    name="registrationNumber"
                    value={formData.registrationNumber}
                    onChange={handleInputChange}
                    placeholder="Your Registration Number"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#732570] ${errors.registrationNumber ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors.registrationNumber && (
                    <p className="text-red-500 text-xs mt-1">{errors.registrationNumber}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industry Type
                </label>
                <input
                  name="industryType"
                  value={formData.industryType}
                  onChange={handleInputChange}
                  placeholder="Your Industry Type"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#732570] ${errors.industryType ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.industryType && (
                  <p className="text-red-500 text-xs mt-1">{errors.industryType}</p>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Email
                  </label>
                  <input
                    name="contactInfo.email"
                    value={formData.contactInfo.email}
                    onChange={handleInputChange}
                    placeholder="example@yourcompany.com"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#732570] ${errors['contactInfo.email'] ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors['contactInfo.email'] && (
                    <p className="text-red-500 text-xs mt-1">{errors['contactInfo.email']}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    name="contactInfo.phone"
                    value={formData.contactInfo.phone}
                    onChange={handleInputChange}
                    placeholder="Your Phone Number"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#732570] ${errors['contactInfo.phone'] ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors['contactInfo.phone'] && (
                    <p className="text-red-500 text-xs mt-1">{errors['contactInfo.phone']}</p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address
                  </label>
                  <input
                    name="contactInfo.address.street"
                    value={formData.contactInfo.address.street}
                    onChange={handleInputChange}
                    placeholder="Your Street Address"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#732570] ${errors['contactInfo.address.street'] ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors['contactInfo.address.street'] && (
                    <p className="text-red-500 text-xs mt-1">{errors['contactInfo.address.street']}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    name="contactInfo.address.city"
                    value={formData.contactInfo.address.city}
                    onChange={handleInputChange}
                    placeholder="Your City"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#732570] ${errors['contactInfo.address.city'] ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors['contactInfo.address.city'] && (
                    <p className="text-red-500 text-xs mt-1">{errors['contactInfo.address.city']}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <input
                    name="contactInfo.address.state"
                    value={formData.contactInfo.address.state}
                    onChange={handleInputChange}
                    placeholder="Your State"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#732570] ${errors['contactInfo.address.state'] ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors['contactInfo.address.state'] && (
                    <p className="text-red-500 text-xs mt-1">{errors['contactInfo.address.state']}</p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zip Code
                  </label>
                  <input
                    name="contactInfo.address.zipCode"
                    value={formData.contactInfo.address.zipCode}
                    onChange={handleInputChange}
                    placeholder="Your Zip Code"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#732570] ${errors['contactInfo.address.zipCode'] ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors['contactInfo.address.zipCode'] && (
                    <p className="text-red-500 text-xs mt-1">{errors['contactInfo.address.zipCode']}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    name="contactInfo.address.country"
                    value={formData.contactInfo.address.country}
                    onChange={handleInputChange}
                    placeholder="Your Country"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#732570] ${errors['contactInfo.address.country'] ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors['contactInfo.address.country'] && (
                    <p className="text-red-500 text-xs mt-1">{errors['contactInfo.address.country']}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Employees
                  </label>
                  <input
                    name="employees.0.number"
                    value={formData.employees[0].number}
                    onChange={handleInputChange}
                    placeholder="Number of Employees"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#732570] ${errors['employees.0.number'] ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors['employees.0.number'] && (
                    <p className="text-red-500 text-xs mt-1">{errors['employees.0.number']}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-8">
                <button 
                  type="button" 
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 text-[#732570] border border-[#732570] rounded-md hover:bg-[#732570]/10 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-[#732570] text-white rounded-md hover:bg-[#5c1e5c] transition-colors flex items-center justify-center"
                >
                  {isSubmitting && (
                    <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                  )}
                  {isSubmitting ? "Saving..." : "Save Profile"}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CompanyProfileModal;
